import React from 'react'
import { Input as InputComponent } from '@/components/ui/input'
import { formatCurrencyAutoLocale } from '@/utils/tools'
import {
    BUDGET_MOVE_NOTE,
    budgetMoveAmountProblem,
    wholeUnitOf,
    describeBudgetMove,
    getBudgetMove,
    getLearningStatusLabel,
    setBudgetMoveDonor,
} from '@/utils/budget-move'
import { setBuilderField } from '@/utils/audience-builder'

const Input = InputComponent as React.FC<React.InputHTMLAttributes<HTMLInputElement>>

interface Donor {
    ad_set_id: string
    name: string
    budget: number
    max_movable: number
    learning_status: string | null
    delivery_status: string | null
    usable: boolean
    message: string | null
}

export interface AddAudienceEligibility {
    eligible: boolean
    reason: string | null
    message: string | null
    max_ad_sets: number
    existing_ad_set_count: number
    available_ad_set_slots: number
    max_personas: number
    min_daily_budget: number | null
    total_budget: number
    currency: string | null
    donors: Donor[]
}

interface BudgetMovePanelProps {
    eligibility: AddAudienceEligibility
    data: Record<string, any>
    onChange: (data: Record<string, any>) => void
    amount: string
    onAmountChange: (value: string) => void
    onAmountBlur?: () => void
    currency?: string
    disabled?: boolean
}

/**
 * Where the daily budget of AI audiences added to an existing campaign comes from: one ad set of the campaign
 * (the donor) and the amount moved from it. Shows the donor's budget before and after and the unchanged total.
 */
const BudgetMovePanel: React.FC<BudgetMovePanelProps> = ({
    eligibility,
    data,
    onChange,
    amount,
    onAmountChange,
    onAmountBlur,
    currency,
    disabled = false,
}) => {
    const format = (minorUnits: number) => formatCurrencyAutoLocale({ amount: minorUnits / 100, currency })
    const chosenId = data?.audienceBuilder?.budgetMove?.donorAdSetId || null
    const donor = eligibility.donors.find((entry) => entry.ad_set_id === chosenId && entry.usable) || null
    const problem =
        amount === '' && !donor
            ? null
            : budgetMoveAmountProblem(amount, donor, eligibility.min_daily_budget, eligibility.currency)
    const move = problem ? null : getBudgetMove(setBuilderField(data, 'acquisitionBudget', amount))

    return (
        <div className="tw:flex tw:flex-col tw:gap-3" data-testid="budget-move-panel">
            <div className="tw:flex tw:flex-col tw:gap-1">
                <span className="tw:text-sm tw:font-medium">Budget for the AI audiences</span>
                <span className="tw:text-sm tw:text-gray-200">
                    The AI audiences get their daily budget from one ad set of this campaign, so the total daily budget
                    of the campaign does not change. This campaign has {eligibility.existing_ad_set_count} ad sets; at
                    most {eligibility.available_ad_set_slots} more can be added.
                </span>
            </div>

            <fieldset className="tw:flex tw:flex-col tw:gap-2" aria-label="Ad set the budget comes from">
                {eligibility.donors.map((entry) => (
                    <label
                        key={entry.ad_set_id}
                        className="tw:flex tw:items-start tw:gap-2 tw:text-sm"
                        data-testid={`budget-donor-${entry.ad_set_id}`}
                    >
                        <input
                            type="radio"
                            name="budget-donor"
                            value={entry.ad_set_id}
                            checked={chosenId === entry.ad_set_id}
                            disabled={!entry.usable || disabled}
                            onChange={() => onChange(setBudgetMoveDonor(data, entry, eligibility))}
                            className="tw:mt-1"
                        />
                        <span className="tw:flex tw:flex-col">
                            <span>{entry.name}</span>
                            <span className="tw:text-xs tw:text-gray-200">
                                {`${format(entry.budget)} a day · ${getLearningStatusLabel(entry.learning_status)}`}
                            </span>
                            {entry.message && <span className="tw:text-xs tw:text-yellow-300">{entry.message}</span>}
                        </span>
                    </label>
                ))}
            </fieldset>

            <div className="tw:flex tw:flex-col tw:gap-1.5 tw:max-w-xs">
                <label htmlFor="budget-move-amount" className="tw:text-sm">
                    Daily amount to move{currency ? ` (${currency})` : ''}
                </label>
                <Input
                    id="budget-move-amount"
                    type="number"
                    min="0"
                    step={wholeUnitOf(eligibility.currency) === 1 ? '0.01' : '1'}
                    value={amount}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => onAmountChange(event.target.value)}
                    onBlur={onAmountBlur}
                    disabled={disabled || !donor}
                />
            </div>

            {problem && (
                <p className="tw:text-sm tw:text-yellow-300" role="status">
                    {problem}
                </p>
            )}
            {move && (
                <div className="tw:flex tw:flex-col tw:gap-1" data-testid="budget-move-summary">
                    {describeBudgetMove(move, format).map((line) => (
                        <p key={line} className="tw:text-sm">
                            {line}
                        </p>
                    ))}
                </div>
            )}
            <p className="tw:text-xs tw:text-gray-200">{BUDGET_MOVE_NOTE}</p>
        </div>
    )
}

export default BudgetMovePanel
